import { useState } from "react";
import { User, Package, MapPin, Settings, LogOut, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useShopifyCustomer } from "@/contexts/ShopifyCustomerContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function UserMenu() {
  const { isAuthenticated, isLoading, customer, initiateLogin, logout } = useShopifyCustomer();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async () => {
    setIsLoggingIn(true);
    try {
      await initiateLogin();
    } catch {
      setIsLoggingIn(false);
    }
  };

  if (isLoading) {
    return (
      <button className="p-2 text-white/60" disabled>
        <User className="w-5 h-5" />
      </button>
    );
  }

  if (!isAuthenticated) {
    return (
      <button
        onClick={handleLogin}
        disabled={isLoggingIn}
        className={`flex items-center gap-1.5 p-2 text-white/90 hover:text-primary transition-colors ${isLoggingIn ? "opacity-70 pointer-events-none" : ""}`}
        aria-label="Sign in"
      >
        {isLoggingIn ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <span className="hidden sm:inline text-sm text-primary">Signing in...</span>
          </>
        ) : (
          <User className="w-5 h-5" />
        )}
      </button>
    );
  }

  const greeting = customer?.firstName ? `Hi, ${customer.firstName}` : "My Account";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 px-3 py-1.5 text-primary hover:text-primary/80 transition-colors text-sm font-medium" aria-label="Account menu">
          <User className="w-4 h-4" />
          <span className="hidden sm:inline">{greeting}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 bg-popover border-border">
        {customer && (
          <div className="px-3 py-2 text-sm text-muted-foreground truncate">
            {customer.displayName || customer.emailAddress?.emailAddress}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account" className="flex items-center gap-2 cursor-pointer">
            <User className="w-4 h-4" /> My Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/orders" className="flex items-center gap-2 cursor-pointer">
            <Package className="w-4 h-4" /> Orders
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/addresses" className="flex items-center gap-2 cursor-pointer">
            <MapPin className="w-4 h-4" /> Addresses
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/profile" className="flex items-center gap-2 cursor-pointer">
            <Settings className="w-4 h-4" /> Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="flex items-center gap-2 cursor-pointer text-destructive">
          <LogOut className="w-4 h-4" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
